import { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell } from 'lucide-react';
import { useNotificationsStore } from '../../store/notificationsStore';
import { useShowRewards, useShowStreaks } from '../../hooks/useShowRewards';

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export function NotificationBell({ childId }: { childId: string }) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const allNotifications = useNotificationsStore((s) => s.notifications);
  const markRead = useNotificationsStore((s) => s.markRead);
  const markAllRead = useNotificationsStore((s) => s.markAllRead);
  const showRewards = useShowRewards();
  const showStreaks = useShowStreaks();

  // Filter at render time so toggling the parent setting hides existing
  // reward-economy and streak notifications without deleting their records —
  // flipping back on restores them.
  const notifications = useMemo(
    () =>
      allNotifications.filter((n) => {
        if (n.childId !== childId) return false;
        if (!showRewards && (n.type === 'level-up' || n.type === 'shop-unlock')) return false;
        if (!showStreaks && n.type === 'streak-milestone') return false;
        return true;
      }),
    [allNotifications, childId, showRewards, showStreaks]
  );
  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications]
  );

  // Close panel when clicking outside
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleOpen = () => {
    setOpen((prev) => !prev);
  };

  const handleNotificationClick = (id: string) => {
    markRead(id);
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={handleOpen}
        className="relative p-1.5 rounded-full hover:bg-black/5 transition-colors"
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
      >
        <Bell className="w-5 h-5" style={{ color: 'var(--color-theme-text-secondary, #6b7280)' }} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 leading-none">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            ref={panelRef}
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="absolute right-0 top-full mt-2 w-72 max-h-80 overflow-y-auto rounded-xl shadow-xl z-50 border"
            style={{
              backgroundColor: 'var(--color-theme-surface-bg, #ffffff)',
              borderColor: 'var(--color-theme-surface-border, #e5e7eb)',
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: 'var(--color-theme-surface-border, #e5e7eb)' }}>
              <span className="text-sm font-bold" style={{ color: 'var(--color-theme-text-primary, #1f2937)' }}>
                Notifications
              </span>
              {unreadCount > 0 && (
                <button
                  onClick={() => markAllRead(childId)}
                  className="text-xs font-medium text-blue-500 hover:text-blue-600 transition-colors"
                >
                  Mark all read
                </button>
              )}
            </div>

            {/* Notification list */}
            {notifications.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm" style={{ color: 'var(--color-theme-text-secondary, #9ca3af)' }}>
                No notifications yet
              </div>
            ) : (
              <div>
                {notifications.slice(0, 20).map((n) => (
                  <button
                    key={n.id}
                    onClick={() => handleNotificationClick(n.id)}
                    className="w-full flex items-start gap-2.5 px-3 py-2.5 text-left hover:bg-black/5 transition-colors"
                  >
                    {/* Unread dot */}
                    <div className="mt-1.5 flex-shrink-0 w-2 h-2">
                      {!n.read && (
                        <div className="w-2 h-2 rounded-full bg-blue-500" />
                      )}
                    </div>
                    {/* Emoji */}
                    <span className="text-lg flex-shrink-0 leading-none mt-0.5">{n.emoji}</span>
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm leading-tight ${!n.read ? 'font-semibold' : 'font-medium'}`}
                        style={{ color: 'var(--color-theme-text-primary, #1f2937)' }}
                      >
                        {n.title}
                      </p>
                      <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--color-theme-text-secondary, #9ca3af)' }}>
                        {n.description}
                      </p>
                      <p className="text-[10px] mt-0.5" style={{ color: 'var(--color-theme-text-secondary, #9ca3af)' }}>
                        {relativeTime(n.timestamp)}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
